import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { usePartsAndLocations } from "@/hooks/usePartsAndLocations";
import { apiFetch } from "@/utils/api";

interface ReceiptRow {
  id: string;
  receipt_number: string;
  vendor_source: string;
  received_by: string;
  date_received: string;
  status: string;
  created_at: string;
}

interface LineDraft {
  partId: string;
  quantity: number;
  locationId: string;
  serialNotes: string;
}

export function ReceiptsPage() {
  const { receiptId } = useParams();
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState<ReceiptRow[]>([]);
  const [detail, setDetail] = useState<{
    receipt: Record<string, unknown>;
    lines: Record<string, unknown>[];
  } | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const { parts, locations, loading: refsLoading } = usePartsAndLocations();

  const loadList = async (q?: string) => {
    const qs = q ? `?q=${encodeURIComponent(q)}` : "";
    const data = await apiFetch<{ receipts: ReceiptRow[] }>(`/receipts${qs}`);
    setReceipts(data.receipts);
  };

  useEffect(() => {
    (async () => {
      try {
        if (receiptId) {
          const data = await apiFetch<{ receipt: Record<string, unknown>; lines: Record<string, unknown>[] }>(
            `/receipts/${receiptId}`,
          );
          setDetail(data);
        } else {
          await loadList();
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [receiptId]);

  const postReceipt = async () => {
    if (!receiptId) return;
    try {
      await apiFetch(`/receipts/${receiptId}/post`, { method: "POST" });
      const data = await apiFetch<{ receipt: Record<string, unknown>; lines: Record<string, unknown>[] }>(
        `/receipts/${receiptId}`,
      );
      setDetail(data);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Post failed");
    }
  };

  if (loading || refsLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (receiptId && detail) {
    const r = detail.receipt;
    return (
      <div className="space-y-4">
        <Link to="/receipts" className="text-sm text-[#1e3a4f] hover:underline">
          ← Back to Receiving
        </Link>
        <PageHeader
          title={String(r.receipt_number)}
          subtitle={`${String(r.vendor_source || "—")} · ${String(r.date_received)}`}
          actions={
            r.status !== "posted" ? (
              <Button onClick={postReceipt} className="bg-[#1e3a4f] hover:bg-[#163041]">
                Post Receipt
              </Button>
            ) : undefined
          }
        />
        <div className="flex gap-4 text-sm">
          <span>Received by: {String(r.received_by)}</span>
          <StatusBadge status={String(r.status)} />
        </div>
        {r.notes ? <p className="text-sm text-muted-foreground">{String(r.notes)}</p> : null}
        <DataTable
          columns={[
            { key: "part", header: "Part", render: (l) => String(l.part_name) },
            { key: "qty", header: "Qty", className: "text-right", render: (l) => String(l.quantity) },
            { key: "loc", header: "Location", render: (l) => String(l.location_name || "—") },
            { key: "serial", header: "Serial Notes", render: (l) => String(l.serial_notes || "") },
          ]}
          rows={detail.lines}
          rowKey={(l) => String(l.id)}
        />
      </div>
    );
  }

  if (showNew) {
    return (
      <NewReceiptForm
        parts={parts}
        locations={locations}
        onCancel={() => setShowNew(false)}
        onCreated={(id) => navigate(`/receipts/${id}`)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Receiving"
        actions={
          <Button onClick={() => setShowNew(true)} className="bg-[#1e3a4f] hover:bg-[#163041]">
            New Receiving
          </Button>
        }
      />
      <form
        className="flex gap-2 max-w-md"
        onSubmit={(e) => {
          e.preventDefault();
          loadList(search).catch(() => {});
        }}
      >
        <Input placeholder="Search receiving…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>
      <DataTable
        columns={[
          {
            key: "num",
            header: "Receipt #",
            render: (r) => (
              <Link to={`/receipts/${r.id}`} className="font-medium text-[#1e3a4f] hover:underline">
                {r.receipt_number}
              </Link>
            ),
          },
          { key: "vendor", header: "Source", render: (r) => r.vendor_source || "—" },
          { key: "date", header: "Date", render: (r) => r.date_received },
          { key: "by", header: "Received By", render: (r) => r.received_by },
          { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
        ]}
        rows={receipts}
        rowKey={(r) => r.id}
      />
    </div>
  );
}

function NewReceiptForm({
  parts,
  locations,
  onCancel,
  onCreated,
}: {
  parts: { id: string; part_name: string; part_number: string }[];
  locations: { id: string; name: string }[];
  onCancel: () => void;
  onCreated: (id: string) => void;
}) {
  const [vendorSource, setVendorSource] = useState("");
  const [receivedBy, setReceivedBy] = useState("");
  const [dateReceived, setDateReceived] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([
    { partId: "", quantity: 1, locationId: "", serialNotes: "" },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [options, setOptions] = useState<{
    vendorSources: string[];
    requireReceivedBy: boolean;
    requireSerialNotes: boolean;
  } | null>(null);

  useEffect(() => {
    apiFetch<{ options: typeof options extends null ? never : NonNullable<typeof options> }>(
      "/receipt-options",
    )
      .then((d) => {
        setOptions(d.options);
      })
      .catch(() => {});
  }, []);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    if (options?.requireReceivedBy && !receivedBy.trim()) {
      setError("Received by is required.");
      setSubmitting(false);
      return;
    }
    const validLines = lines.filter((l) => l.partId && l.quantity > 0);
    if (options?.requireSerialNotes && validLines.some((l) => !l.serialNotes.trim())) {
      setError("Serial notes are required on all line items.");
      setSubmitting(false);
      return;
    }
    try {
      const data = await apiFetch<{ id: string }>("/receipts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vendorSource,
          receivedBy,
          dateReceived,
          notes,
          lines: lines.filter((l) => l.partId && l.quantity > 0),
        }),
      });
      onCreated(data.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create");
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="New Receiving" />
      <div className="bg-white rounded-lg shadow p-6 space-y-4 max-w-2xl">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Source / Vendor</Label>
            {options?.vendorSources.length ? (
              <>
                <Select value={vendorSource} onChange={(e) => setVendorSource(e.target.value)}>
                  <option value="">Select or type below…</option>
                  {options.vendorSources.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </Select>
                <Input
                  className="mt-2"
                  placeholder="Or enter custom source"
                  value={vendorSource}
                  onChange={(e) => setVendorSource(e.target.value)}
                />
              </>
            ) : (
              <Input value={vendorSource} onChange={(e) => setVendorSource(e.target.value)} />
            )}
          </div>
          <div className="space-y-1.5">
            <Label>
              Received By{options?.requireReceivedBy ? " *" : ""}
            </Label>
            <Input value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} required={options?.requireReceivedBy} />
          </div>
          <div className="space-y-1.5">
            <Label>Date Received</Label>
            <Input type="date" value={dateReceived} onChange={(e) => setDateReceived(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </div>
        <div className="space-y-2">
          <Label>Line Items</Label>
          {lines.map((line, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-4 items-end border-b pb-2">
              <Select value={line.partId} onChange={(e) => {
                const next = [...lines];
                next[i] = { ...next[i], partId: e.target.value };
                setLines(next);
              }}>
                <option value="">Select part…</option>
                {parts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.part_name}{p.part_number ? ` (${p.part_number})` : ""}
                  </option>
                ))}
              </Select>
              <Input
                type="number"
                min={1}
                value={line.quantity}
                onChange={(e) => {
                  const next = [...lines];
                  next[i] = { ...next[i], quantity: Number(e.target.value) };
                  setLines(next);
                }}
              />
              <Select value={line.locationId} onChange={(e) => {
                const next = [...lines];
                next[i] = { ...next[i], locationId: e.target.value };
                setLines(next);
              }}>
                <option value="">Default location</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </Select>
              <Input
                placeholder={options?.requireSerialNotes ? "Serial notes *" : "Serial notes"}
                value={line.serialNotes}
                onChange={(e) => {
                  const next = [...lines];
                  next[i] = { ...next[i], serialNotes: e.target.value };
                  setLines(next);
                }}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setLines([...lines, { partId: "", quantity: 1, locationId: "", serialNotes: "" }])}
          >
            Add Line
          </Button>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#1e3a4f] hover:bg-[#163041]">
            {submitting ? "Saving…" : "Create Receiving"}
          </Button>
        </div>
      </div>
    </div>
  );
}
